import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinDatePipe } from '../../shared/design-system/ain-date.pipe';
import { JalaliDateInputComponent } from '../../shared/design-system/jalali-date-input.component';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { PartySelectorComponent } from '../../shared/design-system/party-selector.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import {
  CreateInsuranceProducerPayload,
  InsuranceAgencyListItem,
  InsuranceApiService,
  InsuranceCommissionPlanListItem,
  InsuranceProducerAgreement,
  InsuranceProducerDetail,
  InsuranceProducerStatusTransition,
  InsuranceProductListItem,
  InsuranceTerritory,
  UpdateInsuranceProducerPayload
} from './insurance-api.service';

type ProducerTab = 'summary' | 'agreements' | 'statusHistory';

@Component({
  standalone: true,
  selector: 'ain-insurance-producer-detail',
  imports: [
    AinDatePipe,
    AinPageComponent,
    AinStateComponent,
    InfoHintComponent,
    RouterLink,
    FormsModule,
    InputTextModule,
    SelectModule,
    TableModule,
    DialogModule,
    ButtonModule,
    CheckboxModule,
    JalaliDateInputComponent,
    PartySelectorComponent
  ],
  template: `
    <ain-page [title]="pageTitle()" [description]="t('insurance.producerDetail.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'insurance.producers.help.detail.title'"
        [descriptionKey]="'insurance.producers.help.detail.description'"
        [bulletKeys]="[
          'insurance.producers.help.detail.bullets.profile',
          'insurance.producers.help.detail.bullets.agreements',
          'insurance.producers.help.detail.bullets.license'
        ]" />
      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else if (permissionDenied()) {
        <ain-state type="error" [title]="t('insurance.states.noPermission')" [message]="t('insurance.states.noPermissionMessage')" />
      } @else if (isCreateMode()) {
        <div class="toolbar">
          <a class="back-link" [routerLink]="['/insurance/producers']">{{ t('catalog.action.backToList') }}</a>
          <button pButton type="button" [label]="t('common.save')" [disabled]="saving()" (click)="saveCreate()"></button>
        </div>
        <section class="card">
          <div class="section-title">
            <h3>{{ t('insurance.producerCreate.title') }}</h3>
            <ain-info-hint
              [titleKey]="'insurance.producers.help.create.title'"
              [descriptionKey]="'insurance.producers.help.create.description'"
              [bulletKeys]="[
                'insurance.producers.help.create.bullets.legalEntity',
                'insurance.producers.help.create.bullets.license'
              ]" />
          </div>
          <form class="grid" (ngSubmit)="saveCreate()">
            <label><span>{{ t('insurance.producers.fields.legalEntityId') }}</span><ain-party-selector [(ngModel)]="createForm.legalEntityId" name="legalEntityId" /></label>
            <label><span>{{ t('insurance.producers.fields.code') }}</span><input pInputText [(ngModel)]="createForm.code" name="code" /></label>
            <label><span>{{ t('insurance.producers.fields.name') }}</span><input pInputText [(ngModel)]="createForm.name" name="name" /></label>
            <label>
              <span>{{ t('insurance.producers.fields.type') }}</span>
              <p-select [options]="producerTypeOptions" optionValue="value" [(ngModel)]="createForm.producerType" name="producerType" appendTo="body">
                <ng-template pTemplate="selectedItem" let-item>{{ item ? producerTypeLabel(item.value) : '' }}</ng-template>
                <ng-template pTemplate="item" let-item>{{ producerTypeLabel(item.value) }}</ng-template>
              </p-select>
            </label>
            <label><span>{{ t('insurance.producers.fields.partyId') }}</span><ain-party-selector [(ngModel)]="createForm.partyId" name="partyId" /></label>
            <label><span>{{ t('insurance.producers.fields.licenseNumber') }}</span><input pInputText [(ngModel)]="createForm.licenseNumber" name="licenseNumber" /></label>
            <label><span>{{ t('insurance.producers.fields.licenseExpiry') }}</span><ain-date-input [(ngModel)]="createForm.licenseExpiryUtc" name="licenseExpiryUtc" /></label>
          </form>
          @if (error()) { <p class="error">{{ error() }}</p> }
        </section>
      } @else if (!producer()) {
        <ain-state type="error" [title]="t('insurance.states.notFound')" [message]="t('insurance.states.notFoundMessage')" />
      } @else {
        <div class="toolbar">
          <a class="back-link" [routerLink]="['/insurance/producers']">{{ t('catalog.action.backToList') }}</a>
          <div class="toolbar-actions">
            <button pButton type="button" severity="secondary" [label]="t('insurance.producerDetail.actions.validateLicense')" [disabled]="saving()" (click)="validateLicense()"></button>
            <button pButton type="button" [label]="t('common.save')" [disabled]="saving()" (click)="saveEdit()"></button>
          </div>
        </div>

        <section class="card">
          <div class="section-title">
            <h3>{{ t('insurance.producerDetail.tabs.summary') }}</h3>
            <ain-info-hint
              [titleKey]="'insurance.producers.help.summary.title'"
              [descriptionKey]="'insurance.producers.help.summary.description'"
              [bulletKeys]="[
                'insurance.producers.help.summary.bullets.identity',
                'insurance.producers.help.summary.bullets.license'
              ]" />
          </div>
          <div class="summary">
            <span>{{ t('insurance.producers.fields.code') }}: <strong>{{ producer()!.code }}</strong></span>
            <span>{{ t('insurance.producers.fields.name') }}: <strong>{{ producer()!.name }}</strong></span>
            <span>{{ t('insurance.producers.fields.type') }}: <strong>{{ producerTypeLabel(producer()!.producerType) }}</strong></span>
            <span>{{ t('insurance.producers.fields.licenseNumber') }}: <strong>{{ producer()!.licenseNumber }}</strong></span>
            <span>{{ t('insurance.producers.fields.licenseExpiry') }}: <strong>{{ producer()!.licenseExpiryUtc | ainDate }}</strong></span>
            <span>{{ t('columns.status') }}: <strong>{{ producerStatusLabel(producer()!.status) }}</strong></span>
          </div>
        </section>

        <section class="card">
          <div class="tabs">
            <button type="button" [class.active]="activeTab() === 'summary'" (click)="activeTab.set('summary')">{{ t('insurance.producerDetail.tabs.summary') }}</button>
            <button type="button" [class.active]="activeTab() === 'agreements'" (click)="activeTab.set('agreements')">{{ t('insurance.producerDetail.tabs.agreements') }}</button>
            <button type="button" [class.active]="activeTab() === 'statusHistory'" (click)="activeTab.set('statusHistory')">{{ t('insurance.producerDetail.tabs.statusHistory') }}</button>
          </div>

          @if (activeTab() === 'summary') {
            <div class="lines-header">
              <h4>{{ t('insurance.producerDetail.tabs.summary') }}</h4>
              <ain-info-hint
                [titleKey]="'insurance.producers.help.summary.title'"
                [descriptionKey]="'insurance.producers.help.summary.description'"
                [bulletKeys]="[
                  'insurance.producers.help.summary.bullets.identity',
                  'insurance.producers.help.summary.bullets.license'
                ]" />
            </div>
            <form class="grid">
              <label><span>{{ t('insurance.producers.fields.name') }}</span><input pInputText [(ngModel)]="editForm.name" name="editName" /></label>
              <label>
                <span>{{ t('insurance.producers.fields.type') }}</span>
                <p-select [options]="producerTypeOptions" optionValue="value" [(ngModel)]="editForm.producerType" name="editType" appendTo="body">
                  <ng-template pTemplate="selectedItem" let-item>{{ item ? producerTypeLabel(item.value) : '' }}</ng-template>
                  <ng-template pTemplate="item" let-item>{{ producerTypeLabel(item.value) }}</ng-template>
                </p-select>
              </label>
              <label><span>{{ t('insurance.producers.fields.partyId') }}</span><ain-party-selector [(ngModel)]="editForm.partyId" name="editPartyId" /></label>
              <label><span>{{ t('insurance.producers.fields.licenseNumber') }}</span><input pInputText [(ngModel)]="editForm.licenseNumber" name="editLicenseNumber" /></label>
              <label><span>{{ t('insurance.producers.fields.licenseExpiry') }}</span><ain-date-input [(ngModel)]="editForm.licenseExpiryUtc" name="editLicenseExpiryUtc" /></label>
            </form>
          } @else if (activeTab() === 'agreements') {
            <div class="lines-header">
              <h4>{{ t('insurance.producerDetail.tabs.agreements') }}</h4>
              <div class="lines-actions">
                <ain-info-hint
                  [titleKey]="'insurance.producers.help.agreements.title'"
                  [descriptionKey]="'insurance.producers.help.agreements.description'"
                  [bulletKeys]="[
                    'insurance.producers.help.agreements.bullets.scope',
                    'insurance.producers.help.agreements.bullets.commission'
                  ]" />
                <button pButton type="button" [label]="t('insurance.producerDetail.agreements.add')" (click)="openAgreementCreate()"></button>
                <button pButton type="button" severity="secondary" [label]="t('common.save')" [disabled]="saving()" (click)="saveAgreements()"></button>
              </div>
            </div>
            @if (agreements().length === 0) {
              <p>{{ t('insurance.producerDetail.agreements.empty') }}</p>
            } @else {
              <p-table [value]="agreements()" styleClass="lines-table">
                <ng-template pTemplate="header">
                  <tr>
                    <th>{{ t('insurance.producerDetail.agreements.agency') }}</th>
                    <th>{{ t('insurance.producerDetail.agreements.productVersion') }}</th>
                    <th>{{ t('insurance.producerDetail.agreements.commissionPlan') }}</th>
                    <th>{{ t('insurance.producerDetail.agreements.effectiveFrom') }}</th>
                    <th>{{ t('insurance.producerDetail.agreements.effectiveTo') }}</th>
                    <th>{{ t('insurance.producerDetail.agreements.territory') }}</th>
                    <th>{{ t('insurance.producerDetail.agreements.isExclusive') }}</th>
                    <th></th>
                  </tr>
                </ng-template>
                <ng-template pTemplate="body" let-row let-i="rowIndex">
                  <tr>
                    <td>{{ agencyLabel(row.agencyId) }}</td>
                    <td>{{ productLabel(row.productVersionId) }}</td>
                    <td>{{ commissionPlanLabel(row.commissionPlanId) }}</td>
                    <td>{{ row.effectiveFromUtc | ainDate }}</td>
                    <td>{{ row.effectiveToUtc ? (row.effectiveToUtc | ainDate) : '-' }}</td>
                    <td>{{ territoryLabel(row.territoryId) }}</td>
                    <td>{{ row.isExclusive ? t('common.yes') : t('common.no') }}</td>
                    <td class="actions-cell">
                      <button pButton type="button" text="true" [label]="t('common.edit')" (click)="openAgreementEdit(i)"></button>
                      <button pButton type="button" text="true" severity="danger" [label]="t('common.delete')" (click)="removeAgreement(i)"></button>
                    </td>
                  </tr>
                </ng-template>
              </p-table>
            }
          } @else {
            <div class="lines-header">
              <h4>{{ t('insurance.producerDetail.tabs.statusHistory') }}</h4>
              <ain-info-hint
                [titleKey]="'insurance.producers.help.statusHistory.title'"
                [descriptionKey]="'insurance.producers.help.statusHistory.description'"
                [bulletKeys]="[
                  'insurance.producers.help.statusHistory.bullets.transitions',
                  'insurance.producers.help.statusHistory.bullets.validate'
                ]" />
            </div>
            @if (statusTransitions().length === 0) {
              <p>{{ t('insurance.producerDetail.statusHistory.empty') }}</p>
            } @else {
              <p-table [value]="statusTransitions()" styleClass="lines-table">
                <ng-template pTemplate="header">
                  <tr>
                    <th>{{ t('insurance.producerDetail.statusHistory.from') }}</th>
                    <th>{{ t('insurance.producerDetail.statusHistory.to') }}</th>
                    <th>{{ t('insurance.producerDetail.statusHistory.reason') }}</th>
                    <th>{{ t('insurance.producerDetail.statusHistory.changedAt') }}</th>
                  </tr>
                </ng-template>
                <ng-template pTemplate="body" let-row>
                  <tr>
                    <td>{{ row.fromStatus ? producerStatusLabel(row.fromStatus) : '-' }}</td>
                    <td>{{ producerStatusLabel(row.toStatus) }}</td>
                    <td>{{ row.reason || '-' }}</td>
                    <td>{{ row.changedAtUtc | ainDate }}</td>
                  </tr>
                </ng-template>
              </p-table>
            }
          }
        </section>

        @if (error()) { <p class="error">{{ error() }}</p> }

        <p-dialog [header]="agreementDialogTitle()" [modal]="true" [(visible)]="agreementDialogVisible" [style]="{ width: '760px' }">
          <div class="dialog-grid">
            <label>
              <span>{{ t('insurance.producerDetail.agreements.agency') }}</span>
              <p-select [options]="agencyOptions()" optionLabel="label" optionValue="value" [(ngModel)]="agreementDraft.agencyId" name="agreementAgencyId" [filter]="true" [showClear]="true" appendTo="body" />
            </label>
            <label>
              <span>{{ t('insurance.producerDetail.agreements.productVersion') }}</span>
              <p-select [options]="productOptions()" optionLabel="label" optionValue="value" [(ngModel)]="agreementDraft.productVersionId" name="agreementProductVersionId" [filter]="true" [showClear]="true" appendTo="body" />
            </label>
            <label>
              <span>{{ t('insurance.producerDetail.agreements.commissionPlan') }}</span>
              <p-select [options]="commissionPlanOptions()" optionLabel="label" optionValue="value" [(ngModel)]="agreementDraft.commissionPlanId" name="agreementCommissionPlanId" [filter]="true" [showClear]="true" appendTo="body" />
            </label>
            <label><span>{{ t('insurance.producerDetail.agreements.effectiveFrom') }}</span><ain-date-input [(ngModel)]="agreementDraft.effectiveFromUtc" name="agreementEffectiveFrom" /></label>
            <label><span>{{ t('insurance.producerDetail.agreements.effectiveTo') }}</span><ain-date-input [(ngModel)]="agreementDraft.effectiveToUtc" name="agreementEffectiveTo" /></label>
            <label>
              <span>{{ t('insurance.producerDetail.agreements.territory') }}</span>
              <p-select [options]="territoryOptions()" optionLabel="label" optionValue="value" [(ngModel)]="agreementDraft.territoryId" name="agreementTerritoryId" [filter]="true" [showClear]="true" appendTo="body" />
            </label>
            <label class="checkbox wide">
              <p-checkbox binary="true" [(ngModel)]="agreementDraft.isExclusive" name="agreementIsExclusive" />
              <span>{{ t('insurance.producerDetail.agreements.isExclusive') }}</span>
            </label>
          </div>
          <ng-template pTemplate="footer">
            <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="agreementDialogVisible = false"></button>
            <button pButton type="button" [label]="t('common.save')" (click)="saveAgreementDialog()"></button>
          </ng-template>
        </p-dialog>
      }
    </ain-page>
  `,
  styles: [`
    .toolbar { margin-bottom: 12px; display: flex; justify-content: space-between; align-items: center; gap: 8px; flex-wrap: wrap; }
    .toolbar-actions { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; }
    .back-link { color: #1d4ed8; font-weight: 600; text-decoration: none; }
    .card { border: 1px solid #e5e7eb; border-radius: 12px; padding: 12px; margin-bottom: 10px; }
    .section-title { display: flex; align-items: center; gap: 8px; margin-bottom: 10px; }
    .section-title h3 { margin: 0; }
    .summary { display: flex; gap: 16px; flex-wrap: wrap; font-size: 14px; }
    .tabs { display: flex; flex-wrap: wrap; gap: 8px; border-bottom: 1px solid #e2e8f0; padding-bottom: 6px; margin-bottom: 10px; }
    .tabs button { border: 0; background: transparent; padding: 6px 10px; border-radius: 8px; cursor: pointer; color: #334155; }
    .tabs button.active { background: #dbeafe; color: #1e40af; font-weight: 600; }
    .lines-header { display: flex; justify-content: space-between; align-items: center; gap: 8px; margin-bottom: 10px; }
    .lines-header h4 { margin: 0; }
    .lines-actions { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
    .grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 10px; }
    .dialog-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; }
    .dialog-grid .wide { grid-column: 1 / -1; }
    label { display: grid; gap: 6px; }
    .checkbox { display: flex; gap: 8px; align-items: center; }
    .actions-cell { display: flex; gap: 4px; justify-content: flex-end; white-space: nowrap; }
    .error { color: #b91c1c; }
    @media (max-width: 760px) { .grid, .dialog-grid { grid-template-columns: 1fr; } }
  `]
})
export class InsuranceProducerDetailComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly api = inject(InsuranceApiService);
  private readonly localization = inject(LocalizationService);
  readonly loading = signal(true);
  readonly saving = signal(false);
  readonly error = signal('');
  readonly permissionDenied = signal(false);
  readonly producer = signal<InsuranceProducerDetail | null>(null);
  readonly isCreateMode = signal(false);
  readonly activeTab = signal<ProducerTab>('summary');
  readonly agreements = signal<InsuranceProducerAgreement[]>([]);
  readonly statusTransitions = signal<InsuranceProducerStatusTransition[]>([]);
  readonly agencies = signal<InsuranceAgencyListItem[]>([]);
  readonly territories = signal<InsuranceTerritory[]>([]);
  readonly products = signal<InsuranceProductListItem[]>([]);
  readonly commissionPlans = signal<InsuranceCommissionPlanListItem[]>([]);

  agreementDialogVisible = false;
  private agreementDialogMode: 'create' | 'edit' = 'create';
  private agreementDialogIndex = -1;
  agreementDraft: InsuranceProducerAgreement = this.emptyAgreementDraft();

  createForm: CreateInsuranceProducerPayload = {
    legalEntityId: '',
    code: '',
    name: '',
    producerType: 'Agent',
    partyId: '',
    licenseNumber: '',
    licenseExpiryUtc: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString()
  };

  editForm: UpdateInsuranceProducerPayload = {
    name: '',
    producerType: 'Agent',
    partyId: '',
    licenseNumber: '',
    licenseExpiryUtc: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString()
  };

  readonly producerTypeOptions = [
    { value: 'Agent', labelKey: 'insurance.producers.types.agent' },
    { value: 'Broker', labelKey: 'insurance.producers.types.broker' },
    { value: 'Employee', labelKey: 'insurance.producers.types.employee' },
    { value: 'Bancassurance', labelKey: 'insurance.producers.types.bancassurance' }
  ];

  readonly producerStatusOptions = [
    { value: 'Active', labelKey: 'insurance.producers.status.active' },
    { value: 'Suspended', labelKey: 'insurance.producers.status.suspended' },
    { value: 'Terminated', labelKey: 'insurance.producers.status.terminated' }
  ];

  ngOnInit(): void {
    this.api.listAgencies().subscribe({ next: items => this.agencies.set(items), error: () => this.agencies.set([]) });
    this.api.listTerritories().subscribe({ next: items => this.territories.set(items), error: () => this.territories.set([]) });
    this.api.listProducts().subscribe({ next: items => this.products.set(items), error: () => this.products.set([]) });
    this.api.listCommissionPlans(true).subscribe({ next: items => this.commissionPlans.set(items), error: () => this.commissionPlans.set([]) });

    const producerId = this.route.snapshot.paramMap.get('producerId');
    const isNewRoute = this.route.snapshot.url.at(-1)?.path === 'new';
    if (isNewRoute) {
      this.isCreateMode.set(true);
      this.loading.set(false);
      return;
    }

    if (!producerId) {
      this.loading.set(false);
      return;
    }

    this.api.getProducer(producerId).subscribe({
      next: item => {
        this.producer.set(item);
        if (item) {
          this.editForm = {
            name: item.name,
            producerType: item.producerType,
            partyId: item.partyId,
            licenseNumber: item.licenseNumber,
            licenseExpiryUtc: item.licenseExpiryUtc
          };
          this.agreements.set(item.agreements);
          this.statusTransitions.set(item.statusTransitions);
        }
        this.loading.set(false);
      },
      error: err => {
        this.permissionDenied.set(err?.status === 403);
        this.error.set(err?.status === 403 ? '' : this.t('insurance.states.loadError'));
        this.loading.set(false);
      }
    });
  }

  pageTitle(): string {
    return this.isCreateMode()
      ? this.t('insurance.producerCreate.title')
      : this.t('insurance.producerDetail.title');
  }

  agencyOptions(): Array<{ label: string; value: string }> {
    return this.agencies().map(item => ({ value: item.id, label: `${item.code} - ${item.name}` }));
  }

  territoryOptions(): Array<{ label: string; value: string }> {
    return this.territories().map(item => ({ value: item.id, label: `${item.code} - ${item.name}` }));
  }

  productOptions(): Array<{ label: string; value: string }> {
    return this.products().map(item => ({ value: item.id, label: `${item.code} - ${item.name}` }));
  }

  commissionPlanOptions(): Array<{ label: string; value: string }> {
    return this.commissionPlans().map(item => ({ value: item.id, label: `${item.code} - ${item.name}` }));
  }

  producerTypeLabel(value: string): string {
    const match = this.producerTypeOptions.find(item => item.value === value);
    return match ? this.t(match.labelKey) : value;
  }

  producerStatusLabel(value: string): string {
    const match = this.producerStatusOptions.find(item => item.value === value);
    return match ? this.t(match.labelKey) : value;
  }

  agencyLabel(agencyId: string): string {
    const match = this.agencies().find(item => item.id === agencyId);
    return match ? `${match.code} - ${match.name}` : '-';
  }

  productLabel(productVersionId: string | null): string {
    if (!productVersionId) {
      return '-';
    }
    const match = this.products().find(item => item.id === productVersionId);
    return match ? `${match.code} - ${match.name}` : '-';
  }

  commissionPlanLabel(commissionPlanId: string): string {
    const match = this.commissionPlans().find(item => item.id === commissionPlanId);
    return match ? `${match.code} - ${match.name}` : '-';
  }

  territoryLabel(territoryId: string | null): string {
    if (!territoryId) {
      return '-';
    }
    const match = this.territories().find(item => item.id === territoryId);
    return match ? `${match.code} - ${match.name}` : '-';
  }

  saveCreate(): void {
    this.error.set('');
    this.saving.set(true);
    this.api.createProducer(this.createForm).subscribe({
      next: item => {
        this.saving.set(false);
        void this.router.navigate(['/insurance/producers', item.id]);
      },
      error: () => {
        this.saving.set(false);
        this.error.set(this.t('insurance.states.loadError'));
      }
    });
  }

  saveEdit(): void {
    const current = this.producer();
    if (!current) {
      return;
    }
    this.error.set('');
    this.saving.set(true);
    this.api.updateProducer(current.id, this.editForm).subscribe({
      next: item => {
        this.producer.set(item);
        this.statusTransitions.set(item.statusTransitions);
        this.saving.set(false);
      },
      error: () => {
        this.saving.set(false);
        this.error.set(this.t('insurance.states.loadError'));
      }
    });
  }

  openAgreementCreate(): void {
    this.agreementDialogMode = 'create';
    this.agreementDialogIndex = -1;
    this.agreementDraft = this.emptyAgreementDraft();
    this.agreementDialogVisible = true;
  }

  openAgreementEdit(index: number): void {
    const row = this.agreements()[index];
    if (!row) {
      return;
    }
    this.agreementDialogMode = 'edit';
    this.agreementDialogIndex = index;
    this.agreementDraft = { ...row };
    this.agreementDialogVisible = true;
  }

  removeAgreement(index: number): void {
    const next = [...this.agreements()];
    next.splice(index, 1);
    this.agreements.set(next);
  }

  saveAgreementDialog(): void {
    if (!this.agreementDraft.agencyId || !this.agreementDraft.commissionPlanId) {
      return;
    }
    const next = [...this.agreements()];
    if (this.agreementDialogMode === 'create') {
      next.push({ ...this.agreementDraft });
    } else if (this.agreementDialogIndex >= 0) {
      next[this.agreementDialogIndex] = { ...this.agreementDraft };
    }
    this.agreements.set(next);
    this.agreementDialogVisible = false;
  }

  agreementDialogTitle(): string {
    return this.agreementDialogMode === 'create'
      ? this.t('insurance.producerDetail.agreements.dialog.create')
      : this.t('insurance.producerDetail.agreements.dialog.edit');
  }

  private emptyAgreementDraft(): InsuranceProducerAgreement {
    return {
      id: '',
      producerId: this.producer()?.id ?? '',
      agencyId: '',
      productVersionId: null,
      commissionPlanId: '',
      effectiveFromUtc: new Date().toISOString(),
      effectiveToUtc: null,
      territoryId: null,
      isExclusive: false
    };
  }

  saveAgreements(): void {
    const current = this.producer();
    if (!current) {
      return;
    }

    const payload = {
      items: this.agreements()
        .filter(item => !!item.agencyId && !!item.commissionPlanId)
        .map(item => ({
          agencyId: item.agencyId,
          productVersionId: item.productVersionId,
          commissionPlanId: item.commissionPlanId,
          effectiveFromUtc: item.effectiveFromUtc,
          effectiveToUtc: item.effectiveToUtc,
          territoryId: item.territoryId,
          isExclusive: !!item.isExclusive
        }))
    };
    this.saving.set(true);
    this.api.upsertProducerAgreements(current.id, payload).subscribe({
      next: rows => {
        this.agreements.set(rows);
        this.saving.set(false);
      },
      error: () => {
        this.saving.set(false);
        this.error.set(this.t('insurance.states.loadError'));
      }
    });
  }

  validateLicense(): void {
    const current = this.producer();
    if (!current || !confirm(this.t('insurance.producerDetail.messages.validateLicenseConfirm'))) {
      return;
    }
    this.saving.set(true);
    this.api.validateProducerLicense(current.id).subscribe({
      next: result => {
        this.saving.set(false);
        this.editForm.licenseExpiryUtc = result.licenseExpiryUtc;
        this.api.getProducer(current.id).subscribe(item => {
          if (item) {
            this.producer.set(item);
            this.statusTransitions.set(item.statusTransitions);
          }
        });
      },
      error: () => {
        this.saving.set(false);
        this.error.set(this.t('insurance.states.loadError'));
      }
    });
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
